import { useState, useEffect, useRef, useCallback } from "react";
import { http, API } from "@/lib/api";
import Layout from "@/components/Layout";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { Pause, Play, RotateCcw, X, Volume2, VolumeX, ChevronLeft, Square, Circle, Wind, Brain, Hand, Clock, Eye } from "lucide-react";
import { useSEO } from "@/hooks/useSEO";

const ICONS = { circle: Circle, square: Square, wind: Wind, brain: Brain, hand: Hand, clock: Clock };

/* ═══ WAKE LOCK HOOK ═══ */
function useWakeLock(active) {
  const wakeLockRef = useRef(null);
  useEffect(() => {
    if (!active || !("wakeLock" in navigator)) return;
    let released = false;
    navigator.wakeLock.request("screen").then(lock => {
      if (released) { lock.release(); return; }
      wakeLockRef.current = lock;
    }).catch(() => {});
    return () => {
      released = true;
      wakeLockRef.current?.release().catch(() => {});
      wakeLockRef.current = null;
    };
  }, [active]);
}

/* ═══ AUDIO ENGINE ═══ */
function useBreathingAudio(soundOn, phase, exerciseId) {
  const audioCtxRef = useRef(null);
  const gainRef = useRef(null);
  const oscRef = useRef(null);

  useEffect(() => {
    if (!soundOn) {
      oscRef.current?.stop();
      oscRef.current = null;
      if (audioCtxRef.current && audioCtxRef.current.state === "running") {
        audioCtxRef.current.suspend().catch(() => {});
      }
      return;
    }
    if (!audioCtxRef.current) {
      try {
        audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
        gainRef.current = audioCtxRef.current.createGain();
        gainRef.current.connect(audioCtxRef.current.destination);
        gainRef.current.gain.value = 0;
      } catch { return; }
    }
    const ctx = audioCtxRef.current;
    if (ctx.state === "suspended") ctx.resume();

    // Gentle tone that follows breathing
    const freq = phase === "inhale" ? 220 : phase === "exhale" ? 180 : 200;
    const vol = (phase === "inhale" || phase === "exhale") ? 0.06 : 0.02;
    if (gainRef.current) {
      gainRef.current.gain.cancelScheduledValues(ctx.currentTime);
      gainRef.current.gain.linearRampToValueAtTime(vol, ctx.currentTime + 0.3);
    }
    if (!oscRef.current) {
      try {
        oscRef.current = ctx.createOscillator();
        oscRef.current.type = "sine";
        oscRef.current.frequency.value = freq;
        oscRef.current.connect(gainRef.current);
        oscRef.current.start();
      } catch { }
    } else {
      oscRef.current.frequency.linearRampToValueAtTime(freq, ctx.currentTime + 0.5);
    }

    return () => {};
  }, [soundOn, phase]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      oscRef.current?.stop();
      oscRef.current = null;
      audioCtxRef.current?.close().catch(() => {});
      audioCtxRef.current = null;
    };
  }, []);
}

export default function BreathingRoom() {
  useSEO("breathing-room");
  const [searchParams] = useSearchParams();
  const isPreview = searchParams.get("preview") === "true";
  const [settings, setSettings] = useState(null);
  const [view, setView] = useState("landing");
  const [activeExercise, setActiveExercise] = useState(null);
  const [chooserStep, setChooserStep] = useState(0);
  const [chooserAnswers, setChooserAnswers] = useState({});
  const [afterResult, setAfterResult] = useState(null);
  const [soundOn, setSoundOn] = useState(false);
  const [prefs, setPrefs] = useState(() => {
    try { return JSON.parse(localStorage.getItem("br_prefs") || "{}"); } catch { return {}; }
  });
  const reducedMotion = prefs.reducedMotion || false;

  // Wake lock — active only during exercises
  useWakeLock(view === "exercise");

  useEffect(() => {
    const hasAdminToken = !!localStorage.getItem("stc_token");
    if (isPreview && hasAdminToken) {
      http.get("/breathing-room/admin/preview-settings")
        .then(r => setSettings(r.data))
        .catch(() => {
          // Fallback to public if admin fetch fails
          http.get("/breathing-room/public/settings").then(r => setSettings(r.data)).catch(() => toast.error("Failed to load"));
        });
    } else {
      http.get("/breathing-room/public/settings").then(r => setSettings(r.data)).catch(() => toast.error("Failed to load"));
    }
  }, [isPreview]);

  useEffect(() => { localStorage.setItem("br_prefs", JSON.stringify(prefs)); }, [prefs]);

  const startExercise = (ex) => {
    setActiveExercise(ex);
    setView("exercise");
    http.post("/breathing-room/public/analytics", { exercise_id: ex.id, event: "start" }).catch(() => {});
  };

  const finishExercise = (dur) => {
    http.post("/breathing-room/public/analytics", { exercise_id: activeExercise?.id, event: "complete", duration: dur }).catch(() => {});
    setView("after");
  };

  const stopExercise = () => { setView("stopped"); };

  const recommend = () => {
    const { q1, q2 } = chooserAnswers;
    if (q1 === "racing" && q2 === "one") return "one_minute_reset";
    if (q1 === "racing") return "box_breathing";
    if (q1 === "tense" && q2 === "one") return "one_minute_reset";
    if (q1 === "tense") return "mirror_breath";
    return "longer_exhale";
  };

  if (!settings) return <Layout><div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center"><div className="w-8 h-8 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin" /></div></Layout>;

  const exercises = (settings.exercises || []).filter(e => e.enabled);

  return (
    <Layout>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,600;1,400&family=DM+Sans:wght@300;400;500&display=swap');
        .font-br-h { font-family: 'Cormorant Garamond', serif; }
        .font-br-b { font-family: 'DM Sans', sans-serif; }
      `}</style>

      <div className="min-h-screen bg-[#0A0A0A] text-[#F5F5F0]">
        {/* Preview Banner */}
        {isPreview && (
          <div className="bg-[#D4AF37] text-[#0A0A0A] text-center py-2 px-4 font-br-b text-xs tracking-wide flex items-center justify-center gap-2" data-testid="br-preview-banner">
            <Eye size={14} /> PREVIEW MODE — Changes are not saved. Close this tab to return to admin.
          </div>
        )}

        {/* Global Sound Toggle */}
        {view === "exercise" && (
          <button onClick={() => setSoundOn(!soundOn)} data-testid="br-sound-toggle"
            className="fixed top-20 right-4 z-50 w-10 h-10 rounded-full border border-white/10 bg-[#0A0A0A]/80 backdrop-blur flex items-center justify-center text-zinc-400 hover:text-[#D4AF37] transition-all">
            {soundOn ? <Volume2 size={16} /> : <VolumeX size={16} />}
          </button>
        )}

        <AnimatePresence mode="wait">
          {view === "landing" && <Landing key="landing" s={settings} onStart={() => setView("selector")} />}
          {view === "selector" && (
            <Selector key="selector" exercises={exercises} s={settings}
              onSelect={startExercise}
              onChooseForMe={() => setChooserStep(1)}
              chooserStep={chooserStep} chooserAnswers={chooserAnswers}
              onAnswer={(k, v) => {
                const next = { ...chooserAnswers, [k]: v };
                setChooserAnswers(next);
                if (k === "q2") {
                  const recId = recommend();
                  const ex = exercises.find(e => e.id === recId) || exercises[0];
                  startExercise(ex);
                  setChooserStep(0);
                  setChooserAnswers({});
                } else { setChooserStep(2); }
              }}
              onBack={() => { setChooserStep(0); setChooserAnswers({}); }}
            />
          )}
          {view === "exercise" && activeExercise && (
            <ExerciseRunner key="exercise" exercise={activeExercise} prefs={prefs} reducedMotion={reducedMotion}
              soundOn={soundOn}
              onFinish={finishExercise} onStop={stopExercise} onExit={() => setView("selector")}
            />
          )}
          {view === "stopped" && (
            <StoppedView key="stopped" s={settings} onBack={() => setView("selector")} />
          )}
          {view === "after" && (
            <AfterBreath key="after" s={settings} exercise={activeExercise} exercises={exercises}
              onSelect={startExercise} onJournal={() => setView("journal")} onExit={() => setView("selector")}
            />
          )}
          {view === "journal" && (
            <Journal key="journal" s={settings} exercise={activeExercise} onBack={() => setView("after")} />
          )}
        </AnimatePresence>

        {/* Safety */}
        {(view === "landing" || view === "selector") && (
          <div className="px-6 pb-20 max-w-3xl mx-auto">
            <div className="rounded-xl border border-amber-500/15 bg-amber-500/[0.02] p-5 mb-4">
              <p className="font-br-b text-[11px] text-zinc-400 leading-relaxed">{settings.safety_notice}</p>
            </div>
            <div className="rounded-xl border border-red-500/15 bg-red-500/[0.02] p-5">
              <p className="font-br-b text-[11px] text-zinc-400 leading-relaxed">{settings.crisis_notice}</p>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}

/* ═══ LANDING ═══ */
function Landing({ s, onStart }) {
  return (
    <motion.section initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="flex flex-col items-center justify-center min-h-[80vh] px-6 text-center" data-testid="br-landing">
      <motion.h1 initial={{ y: 30, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.2 }}
        className="font-br-h text-3xl sm:text-4xl lg:text-5xl font-light text-[#F5F5F0] max-w-2xl whitespace-pre-line leading-snug mb-8">{s.headline}</motion.h1>
      <motion.p initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.4 }}
        className="font-br-b text-base text-zinc-400 max-w-xl leading-relaxed mb-10">{s.supporting_text}</motion.p>
      <motion.button initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.6 }}
        onClick={onStart} data-testid="br-start"
        className="bg-[#D4AF37] text-[#0A0A0A] px-10 py-4 rounded-full font-br-b tracking-wide uppercase text-sm hover:shadow-[0_0_25px_rgba(212,175,55,0.3)] transition-all mb-4">{s.primary_btn}</motion.button>
      <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.8 }}
        className="font-br-b text-xs text-zinc-600">{s.reassurance}</motion.p>
    </motion.section>
  );
}

/* ═══ SELECTOR ═══ */
function Selector({ exercises, s, onSelect, onChooseForMe, chooserStep, chooserAnswers, onAnswer, onBack }) {
  if (chooserStep === 1) {
    return (
      <motion.section initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="flex flex-col items-center justify-center min-h-[70vh] px-6 text-center">
        <h2 className="font-br-h text-2xl text-[#F5F5F0] mb-8">Are your thoughts racing, or does your body feel tense?</h2>
        <div className="flex flex-col sm:flex-row gap-4 mb-8">
          <button onClick={() => onAnswer("q1", "racing")} className="border border-white/10 text-zinc-300 px-8 py-4 rounded-full font-br-b text-sm hover:border-[#D4AF37]/40 transition-all">Thoughts are racing</button>
          <button onClick={() => onAnswer("q1", "tense")} className="border border-white/10 text-zinc-300 px-8 py-4 rounded-full font-br-b text-sm hover:border-[#D4AF37]/40 transition-all">Body feels tense</button>
        </div>
        <button onClick={onBack} className="font-br-b text-xs text-zinc-600 hover:text-zinc-400">Go back</button>
      </motion.section>
    );
  }
  if (chooserStep === 2) {
    return (
      <motion.section initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="flex flex-col items-center justify-center min-h-[70vh] px-6 text-center">
        <h2 className="font-br-h text-2xl text-[#F5F5F0] mb-8">Do you have one minute or a few minutes?</h2>
        <div className="flex flex-col sm:flex-row gap-4 mb-8">
          <button onClick={() => onAnswer("q2", "one")} className="border border-white/10 text-zinc-300 px-8 py-4 rounded-full font-br-b text-sm hover:border-[#D4AF37]/40 transition-all">One minute</button>
          <button onClick={() => onAnswer("q2", "few")} className="border border-white/10 text-zinc-300 px-8 py-4 rounded-full font-br-b text-sm hover:border-[#D4AF37]/40 transition-all">A few minutes</button>
        </div>
        <button onClick={onBack} className="font-br-b text-xs text-zinc-600 hover:text-zinc-400">Go back</button>
      </motion.section>
    );
  }

  return (
    <motion.section initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="py-16 px-6 max-w-4xl mx-auto" data-testid="br-selector">
      <h2 className="font-br-h text-3xl md:text-4xl text-[#F5F5F0] text-center mb-12">{s.selector_heading}</h2>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
        {exercises.map(ex => {
          const Icon = ICONS[ex.card_icon] || Circle;
          return (
            <button key={ex.id} onClick={() => onSelect(ex)} data-testid={`br-card-${ex.id}`}
              className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-6 text-left hover:border-[#D4AF37]/30 hover:bg-white/[0.04] transition-all group">
              <Icon size={24} className="text-[#D4AF37] mb-3 group-hover:scale-110 transition-transform" />
              <h3 className="font-br-h text-lg text-[#F5F5F0] mb-1">{ex.card_label}</h3>
              <p className="font-br-b text-xs text-zinc-500">{ex.description}</p>
            </button>
          );
        })}
      </div>
      <div className="text-center">
        <button onClick={onChooseForMe} data-testid="br-choose-for-me"
          className="border border-[#D4AF37] text-[#D4AF37] px-8 py-3.5 rounded-full font-br-b tracking-wide uppercase text-sm hover:bg-[#D4AF37] hover:text-[#0A0A0A] transition-all">
          {s.choose_for_me_btn}
        </button>
      </div>
    </motion.section>
  );
}

/* ═══ EXERCISE RUNNER ═══ */
function ExerciseRunner({ exercise, prefs, reducedMotion, soundOn, onFinish, onStop, onExit }) {
  const ex = exercise;
  if (ex.id === "get_out_of_head") return <GroundingExercise exercise={ex} reducedMotion={reducedMotion} soundOn={soundOn} onFinish={onFinish} onStop={onStop} onExit={onExit} />;
  if (ex.id === "one_minute_reset") return <OneMinuteReset exercise={ex} reducedMotion={reducedMotion} onFinish={onFinish} onStop={onStop} onExit={onExit} />;
  if (ex.id === "belly_breathing") return <BellyBreathing exercise={ex} reducedMotion={reducedMotion} soundOn={soundOn} onFinish={onFinish} onStop={onStop} onExit={onExit} />;
  return <BreathingExercise exercise={ex} prefs={prefs} reducedMotion={reducedMotion} soundOn={soundOn} onFinish={onFinish} onStop={onStop} onExit={onExit} />;
}

/* ═══ CORE BREATHING (Mirror Breath, Box, Longer Exhale) ═══ */
function BreathingExercise({ exercise, prefs, reducedMotion, soundOn, onFinish, onStop, onExit }) {
  const ex = exercise;
  const [duration, setDuration] = useState(ex.default_duration || 180);
  const [started, setStarted] = useState(false);
  const [paused, setPaused] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [phase, setPhase] = useState("inhale");
  const [phaseTime, setPhaseTime] = useState(0);
  const [promptIdx, setPromptIdx] = useState(0);
  const [variant, setVariant] = useState(null);
  const [objectChoice, setObjectChoice] = useState(prefs.objectChoice || (ex.object_choices || [])[0] || "feather");
  const intervalRef = useRef(null);

  // Audio guidance
  useBreathingAudio(soundOn && started && !paused, phase, ex.id);

  const activePattern = variant || { inhale: ex.inhale, exhale: ex.exhale, hold_in: ex.hold_in || 0, hold_out: ex.hold_out || 0 };
  const cycleDur = activePattern.inhale + activePattern.exhale + (activePattern.hold_in || 0) + (activePattern.hold_out || 0);

  useEffect(() => {
    if (!started || paused) { clearInterval(intervalRef.current); return; }
    intervalRef.current = setInterval(() => {
      setElapsed(prev => {
        const next = prev + 0.1;
        if (next >= duration) { clearInterval(intervalRef.current); onFinish(duration); return duration; }
        return next;
      });
      setPhaseTime(prev => prev + 0.1);
    }, 100);
    return () => clearInterval(intervalRef.current);
  }, [started, paused, duration]);

  // Determine current phase
  useEffect(() => {
    const cyclePos = phaseTime % cycleDur;
    const { inhale, hold_in, exhale, hold_out } = activePattern;
    if (cyclePos < inhale) { if (phase !== "inhale") setPhase("inhale"); }
    else if (cyclePos < inhale + (hold_in || 0)) { if (phase !== "hold_in") setPhase("hold_in"); }
    else if (cyclePos < inhale + (hold_in || 0) + exhale) { if (phase !== "exhale") setPhase("exhale"); }
    else { if (phase !== "hold_out") setPhase("hold_out"); }
  }, [phaseTime]);

  // Rotate prompts
  useEffect(() => {
    if (!started || !ex.prompts?.length) return;
    const iv = setInterval(() => setPromptIdx(p => (p + 1) % ex.prompts.length), 8000);
    return () => clearInterval(iv);
  }, [started]);

  const restart = () => { setElapsed(0); setPhaseTime(0); setPhase("inhale"); setPromptIdx(0); };
  const phasePct = (() => {
    const cyclePos = phaseTime % cycleDur;
    const { inhale, hold_in, exhale } = activePattern;
    if (phase === "inhale") return cyclePos / inhale;
    if (phase === "hold_in") return 1;
    if (phase === "exhale") return 1 - (cyclePos - inhale - (hold_in || 0)) / exhale;
    return 0;
  })();
  const PHASE_LABEL = { inhale: "Breathe In", hold_in: "Hold", exhale: "Breathe Out", hold_out: "Hold" };

  if (!started) {
    return (
      <motion.section initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="flex flex-col items-center justify-center min-h-[80vh] px-6 text-center" data-testid={`br-setup-${ex.id}`}>
        <h2 className="font-br-h text-3xl text-[#F5F5F0] mb-2">{ex.title}</h2>
        <p className="font-br-b text-sm text-zinc-400 mb-8">{ex.description}</p>
        {ex.variants && (
          <div className="flex gap-2 mb-6 flex-wrap justify-center">
            {ex.variants.map(v => (
              <button key={v.label} onClick={() => setVariant(v)} className={`px-4 py-2 rounded-full font-br-b text-xs transition-all ${(!variant && v.label === "Standard") || variant?.label === v.label ? "bg-[#D4AF37] text-[#0A0A0A]" : "border border-white/10 text-zinc-400"}`}>{v.label}</button>
            ))}
          </div>
        )}
        {ex.object_choices && (
          <div className="flex gap-2 mb-6 flex-wrap justify-center">
            {ex.object_choices.map(o => (
              <button key={o} onClick={() => setObjectChoice(o)} className={`px-4 py-2 rounded-full font-br-b text-xs capitalize transition-all ${objectChoice === o ? "bg-[#D4AF37] text-[#0A0A0A]" : "border border-white/10 text-zinc-400"}`}>{o}</button>
            ))}
          </div>
        )}
        <div className="flex gap-3 mb-8">
          {(ex.durations || [60, 180, 300]).map(d => (
            <button key={d} onClick={() => setDuration(d)} className={`px-5 py-2.5 rounded-full font-br-b text-xs transition-all ${duration === d ? "bg-white/10 text-[#F5F5F0]" : "border border-white/[0.06] text-zinc-500"}`}>{d < 60 ? `${d}s` : `${d / 60} min`}</button>
          ))}
        </div>
        <button onClick={() => setStarted(true)} className="bg-[#D4AF37] text-[#0A0A0A] px-10 py-4 rounded-full font-br-b tracking-wide uppercase text-sm hover:shadow-[0_0_20px_rgba(212,175,55,0.3)] transition-all" data-testid="br-begin">Begin</button>
        <button onClick={onExit} className="font-br-b text-xs text-zinc-600 mt-4 hover:text-zinc-400">Back to exercises</button>
      </motion.section>
    );
  }

  return (
    <motion.section initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="flex flex-col items-center justify-center min-h-screen px-6 text-center relative" data-testid={`br-active-${ex.id}`}>
      {/* Animation */}
      <div className="relative w-64 h-64 md:w-80 md:h-80 mb-8 flex items-center justify-center">
        {ex.animation === "circle" && (
          <div className={`rounded-full bg-[#D4AF37]/10 border border-[#D4AF37]/30 transition-all ${reducedMotion ? "" : "duration-300"}`}
            style={{ width: `${40 + phasePct * 60}%`, height: `${40 + phasePct * 60}%`, boxShadow: `0 0 ${20 + phasePct * 40}px rgba(212,175,55,${0.1 + phasePct * 0.2})` }} />
        )}
        {ex.animation === "box" && <BoxAnimation phase={phase} phasePct={phasePct} cycleDur={cycleDur} phaseTime={phaseTime} pattern={activePattern} reducedMotion={reducedMotion} />}
        {ex.animation === "object" && <ObjectAnimation phase={phase} phasePct={phasePct} objectChoice={objectChoice} reducedMotion={reducedMotion} />}
      </div>

      {/* Phase label */}
      <p className="font-br-h text-2xl text-[#D4AF37] mb-2">{PHASE_LABEL[phase]}</p>

      {/* Prompt */}
      {ex.prompts?.length > 0 && (
        <p className="font-br-h text-lg text-zinc-400 italic mb-8 h-8">{ex.prompts[promptIdx]}</p>
      )}

      {/* Timer */}
      <div className="flex items-center gap-4 mb-8">
        <span className="font-br-b text-xs text-zinc-500">{Math.floor(elapsed / 60)}:{String(Math.floor(elapsed % 60)).padStart(2, "0")}</span>
        <div className="w-40 h-1 bg-white/[0.06] rounded-full overflow-hidden">
          <div className="h-full bg-[#D4AF37] rounded-full transition-all" style={{ width: `${(elapsed / duration) * 100}%` }} />
        </div>
        <span className="font-br-b text-xs text-zinc-500">{Math.floor(duration / 60)}:{String(Math.floor(duration % 60)).padStart(2, "0")}</span>
      </div>

      {/* Controls */}
      <div className="flex gap-3 mb-6">
        <button onClick={() => setPaused(!paused)} className="w-12 h-12 rounded-full border border-white/10 flex items-center justify-center text-zinc-400 hover:text-[#D4AF37] hover:border-[#D4AF37]/30 transition-all" data-testid="br-pause">
          {paused ? <Play size={18} /> : <Pause size={18} />}
        </button>
        <button onClick={restart} className="w-12 h-12 rounded-full border border-white/10 flex items-center justify-center text-zinc-400 hover:text-[#D4AF37] hover:border-[#D4AF37]/30 transition-all"><RotateCcw size={18} /></button>
        <button onClick={onExit} className="w-12 h-12 rounded-full border border-white/10 flex items-center justify-center text-zinc-400 hover:text-zinc-200 transition-all"><X size={18} /></button>
      </div>

      <button onClick={onStop} data-testid="br-stop"
        className="font-br-b text-xs text-zinc-600 hover:text-red-400 transition-colors">I need to stop</button>
    </motion.section>
  );
}

/* ═══ BOX ANIMATION ═══ */
function BoxAnimation({ phase, phasePct, cycleDur, phaseTime, pattern, reducedMotion }) {
  const size = 200;
  const cyclePos = phaseTime % cycleDur;
  const { inhale, hold_in, exhale, hold_out } = pattern;
  const total = inhale + (hold_in || 0) + exhale + (hold_out || 0);
  const progress = (cyclePos / total);

  // Calculate dot position along the box perimeter
  let x = 0, y = 0;
  const p = progress * 4; // 0-4 for 4 sides
  if (p < 1) { x = p * size; y = 0; } // top: left to right (inhale)
  else if (p < 2) { x = size; y = (p - 1) * size; } // right: top to bottom (hold_in)
  else if (p < 3) { x = size - (p - 2) * size; y = size; } // bottom: right to left (exhale)
  else { x = 0; y = size - (p - 3) * size; } // left: bottom to top (hold_out)

  return (
    <svg width={size + 20} height={size + 20} viewBox={`-10 -10 ${size + 20} ${size + 20}`}>
      <rect x={0} y={0} width={size} height={size} fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth={1} rx={4} />
      {/* Labels */}
      <text x={size / 2} y={-4} textAnchor="middle" className="font-br-b" fill={phase === "inhale" ? "#D4AF37" : "#555"} fontSize={10}>INHALE</text>
      <text x={size + 4} y={size / 2} textAnchor="start" className="font-br-b" fill={phase === "hold_in" ? "#D4AF37" : "#555"} fontSize={10} dominantBaseline="middle">{hold_in ? "HOLD" : ""}</text>
      <text x={size / 2} y={size + 14} textAnchor="middle" className="font-br-b" fill={phase === "exhale" ? "#D4AF37" : "#555"} fontSize={10}>EXHALE</text>
      <text x={-4} y={size / 2} textAnchor="end" className="font-br-b" fill={phase === "hold_out" ? "#D4AF37" : "#555"} fontSize={10} dominantBaseline="middle">{hold_out ? "HOLD" : ""}</text>
      {/* Dot */}
      <circle cx={x} cy={y} r={6} fill="#D4AF37" style={{ filter: "drop-shadow(0 0 8px rgba(212,175,55,0.5))" }}>
        {!reducedMotion && <animate attributeName="r" values="5;7;5" dur="1s" repeatCount="indefinite" />}
      </circle>
      {/* Trail */}
      <line x1={phase === "inhale" ? 0 : phase === "exhale" ? size : x} y1={phase === "inhale" ? 0 : phase === "hold_in" ? 0 : phase === "exhale" ? size : y}
        x2={x} y2={y} stroke="#D4AF37" strokeWidth={2} opacity={0.4} />
    </svg>
  );
}

/* ═══ OBJECT ANIMATION ═══ */
function ObjectAnimation({ phase, phasePct, objectChoice, reducedMotion }) {
  const yPos = phase === "inhale" || phase === "hold_in" ? 30 + (1 - phasePct) * 70 : 30 + phasePct * 70;
  const opacity = phase === "inhale" || phase === "hold_in" ? 0.5 + phasePct * 0.5 : 1 - phasePct * 0.5;
  const OBJ_EMOJI = { feather: "🪶", leaf: "🍂", candle: "🕯", light: "✨" };

  return (
    <div className="w-full h-full flex items-center justify-center relative">
      <div className="absolute inset-0 rounded-full bg-[#D4AF37]/5" />
      <div className={`text-5xl transition-all ${reducedMotion ? "" : "duration-300"}`}
        style={{ transform: `translateY(${yPos - 50}px)`, opacity, filter: `blur(${phase === "exhale" ? phasePct * 0.5 : 0}px)` }}>
        {OBJ_EMOJI[objectChoice] || "✨"}
      </div>
    </div>
  );
}

/* ═══ GROUNDING EXERCISE ═══ */
function GroundingExercise({ exercise, reducedMotion, onFinish, onStop, onExit }) {
  const ex = exercise;
  const [stage, setStage] = useState("intro"); // intro, breathing, grounding, reflection
  const [breathCount, setBreatheCount] = useState(0);
  const [groundingIdx, setGroundingIdx] = useState(0);
  const [items, setItems] = useState([]);
  const [questionIdx, setQuestionIdx] = useState(Math.floor(Math.random() * (ex.reflection_questions || []).length));
  const [phase, setPhase] = useState("inhale");
  const [phaseTime, setPhaseTime] = useState(0);
  const intervalRef = useRef(null);

  const steps = ex.grounding_steps || [];
  const currentStep = steps[groundingIdx];

  // Breathing timer for the 3-breath phase
  useEffect(() => {
    if (stage !== "breathing") { clearInterval(intervalRef.current); return; }
    intervalRef.current = setInterval(() => {
      setPhaseTime(prev => {
        const next = prev + 0.1;
        const cycleDur = (ex.inhale || 4) + (ex.exhale || 6);
        const cyclePos = next % cycleDur;
        if (cyclePos < (ex.inhale || 4)) { if (phase !== "inhale") setPhase("inhale"); }
        else { if (phase !== "exhale") setPhase("exhale"); }
        // Count breaths
        if (Math.floor(next / cycleDur) >= 3) {
          clearInterval(intervalRef.current);
          setStage("grounding");
        }
        return next;
      });
    }, 100);
    return () => clearInterval(intervalRef.current);
  }, [stage, phase]);

  const phasePct = (() => {
    const cycleDur = (ex.inhale || 4) + (ex.exhale || 6);
    const cyclePos = phaseTime % cycleDur;
    if (phase === "inhale") return cyclePos / (ex.inhale || 4);
    return 1 - (cyclePos - (ex.inhale || 4)) / (ex.exhale || 6);
  })();

  const tapItem = () => {
    const newItems = [...items, true];
    setItems(newItems);
    if (newItems.length >= (currentStep?.count || 1)) {
      if (groundingIdx < steps.length - 1) {
        setGroundingIdx(groundingIdx + 1);
        setItems([]);
      } else {
        setStage("reflection");
      }
    }
  };

  if (stage === "intro") {
    return (
      <motion.section initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="flex flex-col items-center justify-center min-h-[80vh] px-6 text-center" data-testid="br-grounding-intro">
        <h2 className="font-br-h text-3xl text-[#F5F5F0] mb-4">{ex.title}</h2>
        <p className="font-br-b text-base text-zinc-400 max-w-md mb-10">{ex.intro}</p>
        <button onClick={() => setStage("breathing")} className="bg-[#D4AF37] text-[#0A0A0A] px-10 py-4 rounded-full font-br-b tracking-wide uppercase text-sm" data-testid="br-begin">Begin with three breaths</button>
        <button onClick={onExit} className="font-br-b text-xs text-zinc-600 mt-4">Back</button>
      </motion.section>
    );
  }

  if (stage === "breathing") {
    return (
      <motion.section initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex flex-col items-center justify-center min-h-screen px-6 text-center" data-testid="br-grounding-breathe">
        <div className="w-48 h-48 mb-8 flex items-center justify-center">
          <div className="rounded-full bg-[#D4AF37]/10 border border-[#D4AF37]/30 transition-all duration-300"
            style={{ width: `${40 + phasePct * 60}%`, height: `${40 + phasePct * 60}%`, boxShadow: `0 0 ${20 + phasePct * 30}px rgba(212,175,55,${0.1 + phasePct * 0.15})` }} />
        </div>
        <p className="font-br-h text-2xl text-[#D4AF37] mb-4">{phase === "inhale" ? "Breathe In" : "Breathe Out"}</p>
        <p className="font-br-b text-xs text-zinc-500">Breath {Math.min(Math.floor(phaseTime / ((ex.inhale || 4) + (ex.exhale || 6))) + 1, 3)} of 3</p>
        <button onClick={onStop} className="font-br-b text-xs text-zinc-600 hover:text-red-400 mt-8">I need to stop</button>
      </motion.section>
    );
  }

  if (stage === "grounding") {
    return (
      <motion.section initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex flex-col items-center justify-center min-h-screen px-6 text-center" data-testid="br-grounding-sense">
        <p className="font-br-b text-xs text-[#D4AF37] tracking-[0.2em] uppercase mb-4">{currentStep?.sense}</p>
        <h2 className="font-br-h text-2xl md:text-3xl text-[#F5F5F0] mb-8 max-w-md">{currentStep?.prompt}</h2>
        <div className="flex gap-3 flex-wrap justify-center mb-8">
          {Array.from({ length: currentStep?.count || 1 }).map((_, i) => (
            <button key={i} onClick={tapItem} disabled={i < items.length}
              className={`w-14 h-14 rounded-full border-2 flex items-center justify-center text-lg transition-all ${
                i < items.length ? "bg-[#D4AF37] border-[#D4AF37] text-[#0A0A0A]" : "border-zinc-700 text-zinc-600 hover:border-[#D4AF37]/50"
              }`} data-testid={`br-tap-${i}`}>
              {i < items.length ? "✓" : i + 1}
            </button>
          ))}
        </div>
        <p className="font-br-b text-xs text-zinc-600">{items.length} of {currentStep?.count}</p>
        <button onClick={onStop} className="font-br-b text-xs text-zinc-600 hover:text-red-400 mt-8">I need to stop</button>
      </motion.section>
    );
  }

  // Reflection
  return (
    <motion.section initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex flex-col items-center justify-center min-h-screen px-6 text-center" data-testid="br-grounding-reflect">
      <div className="w-32 h-32 rounded-full bg-[#D4AF37]/10 border border-[#D4AF37]/20 flex items-center justify-center mb-8">
        <div className="w-20 h-20 rounded-full bg-[#D4AF37]/15" />
      </div>
      <p className="font-br-b text-sm text-zinc-400 mb-4">Now that you are back in the room, ask yourself one honest question.</p>
      <h2 className="font-br-h text-2xl md:text-3xl text-[#F5F5F0] italic max-w-lg mb-8">"{(ex.reflection_questions || [])[questionIdx]}"</h2>
      <button onClick={() => setQuestionIdx((questionIdx + 1) % (ex.reflection_questions || []).length)}
        className="font-br-b text-xs text-[#D4AF37] hover:underline mb-8">Give me another question</button>
      <button onClick={() => onFinish(0)} className="bg-[#D4AF37] text-[#0A0A0A] px-8 py-3 rounded-full font-br-b tracking-wide uppercase text-sm">Continue</button>
      <button onClick={onStop} className="font-br-b text-xs text-zinc-600 hover:text-red-400 mt-4">I need to stop</button>
    </motion.section>
  );
}

/* ═══ BELLY BREATHING ═══ */
function BellyBreathing({ exercise, reducedMotion, onFinish, onStop, onExit }) {
  const ex = exercise;
  const [stage, setStage] = useState("setup"); // setup, instructions, breathing, awareness
  const [instrIdx, setInstrIdx] = useState(0);
  const [duration, setDuration] = useState(ex.default_duration || 180);
  const [elapsed, setElapsed] = useState(0);
  const [paused, setPaused] = useState(false);
  const [phase, setPhase] = useState("inhale");
  const [phaseTime, setPhaseTime] = useState(0);
  const [awareness, setAwareness] = useState(null);
  const intervalRef = useRef(null);

  useEffect(() => {
    if (stage !== "breathing" || paused) { clearInterval(intervalRef.current); return; }
    intervalRef.current = setInterval(() => {
      setElapsed(prev => {
        const next = prev + 0.1;
        if (next >= duration) { clearInterval(intervalRef.current); setStage("awareness"); return duration; }
        return next;
      });
      setPhaseTime(prev => {
        const next = prev + 0.1;
        const cycleDur = (ex.inhale || 4) + (ex.exhale || 6);
        const cyclePos = next % cycleDur;
        setPhase(cyclePos < (ex.inhale || 4) ? "inhale" : "exhale");
        return next;
      });
    }, 100);
    return () => clearInterval(intervalRef.current);
  }, [stage, paused]);

  if (stage === "setup") {
    return (
      <motion.section initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="flex flex-col items-center justify-center min-h-[80vh] px-6 text-center">
        <h2 className="font-br-h text-3xl text-[#F5F5F0] mb-2">{ex.title}</h2>
        <p className="font-br-b text-sm text-zinc-400 mb-8">{ex.description}</p>
        <div className="flex gap-3 mb-8">
          {(ex.durations || [60, 180, 300]).map(d => (
            <button key={d} onClick={() => setDuration(d)} className={`px-5 py-2.5 rounded-full font-br-b text-xs ${duration === d ? "bg-white/10 text-[#F5F5F0]" : "border border-white/[0.06] text-zinc-500"}`}>{d / 60} min</button>
          ))}
        </div>
        <button onClick={() => setStage("instructions")} className="bg-[#D4AF37] text-[#0A0A0A] px-10 py-4 rounded-full font-br-b tracking-wide uppercase text-sm" data-testid="br-begin">Begin</button>
        <button onClick={onExit} className="font-br-b text-xs text-zinc-600 mt-4">Back</button>
      </motion.section>
    );
  }

  if (stage === "instructions") {
    return (
      <motion.section initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex flex-col items-center justify-center min-h-screen px-6 text-center">
        <p className="font-br-h text-2xl text-[#F5F5F0] italic max-w-md mb-8">{(ex.instructions || [])[instrIdx]}</p>
        <button onClick={() => {
          if (instrIdx < (ex.instructions || []).length - 1) setInstrIdx(instrIdx + 1);
          else setStage("breathing");
        }} className="bg-[#D4AF37] text-[#0A0A0A] px-8 py-3 rounded-full font-br-b text-sm">
          {instrIdx < (ex.instructions || []).length - 1 ? "Next" : "Start Breathing"}
        </button>
      </motion.section>
    );
  }

  if (stage === "awareness") {
    if (awareness !== null) {
      return (
        <motion.section initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex flex-col items-center justify-center min-h-screen px-6 text-center">
          <p className="font-br-h text-2xl text-[#D4AF37] italic mb-8">{ex.awareness_response}</p>
          <button onClick={() => onFinish(duration)} className="bg-[#D4AF37] text-[#0A0A0A] px-8 py-3 rounded-full font-br-b text-sm">Continue</button>
        </motion.section>
      );
    }
    return (
      <motion.section initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex flex-col items-center justify-center min-h-screen px-6 text-center">
        <p className="font-br-h text-2xl text-[#F5F5F0] mb-8">{ex.awareness_question}</p>
        <div className="flex flex-col gap-3 max-w-sm w-full">
          {(ex.awareness_options || []).map((opt, i) => (
            <button key={i} onClick={() => setAwareness(i)} className="border border-white/10 text-zinc-300 px-6 py-3 rounded-full font-br-b text-sm hover:border-[#D4AF37]/40 transition-all">{opt}</button>
          ))}
        </div>
      </motion.section>
    );
  }

  // Breathing phase
  const cycleDur = (ex.inhale || 4) + (ex.exhale || 6);
  const cyclePos = phaseTime % cycleDur;
  const phasePct = phase === "inhale" ? cyclePos / (ex.inhale || 4) : 1 - (cyclePos - (ex.inhale || 4)) / (ex.exhale || 6);

  return (
    <motion.section initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex flex-col items-center justify-center min-h-screen px-6 text-center">
      {/* Belly animation - two hands */}
      <div className="relative w-48 h-64 mb-8">
        <div className="absolute top-4 left-1/2 -translate-x-1/2 text-4xl transition-all duration-500" style={{ opacity: 0.5 }}>🤚</div>
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-4xl transition-all duration-500" style={{ transform: `translateY(${phase === "inhale" ? -phasePct * 10 : -(1 - phasePct) * 10}px) translateX(-50%)` }}>🤚</div>
        <div className="absolute inset-0 rounded-full bg-[#D4AF37]/5 transition-all duration-500" style={{ transform: `scaleY(${0.85 + phasePct * 0.15})` }} />
      </div>
      <p className="font-br-h text-2xl text-[#D4AF37] mb-2">{phase === "inhale" ? "Breathe In" : "Breathe Out"}</p>
      <div className="flex items-center gap-4 mb-6">
        <span className="font-br-b text-xs text-zinc-500">{Math.floor(elapsed / 60)}:{String(Math.floor(elapsed % 60)).padStart(2, "0")}</span>
        <div className="w-32 h-1 bg-white/[0.06] rounded-full overflow-hidden"><div className="h-full bg-[#D4AF37] rounded-full" style={{ width: `${(elapsed / duration) * 100}%` }} /></div>
        <span className="font-br-b text-xs text-zinc-500">{duration / 60}:00</span>
      </div>
      <div className="flex gap-3 mb-4">
        <button onClick={() => setPaused(!paused)} className="w-10 h-10 rounded-full border border-white/10 flex items-center justify-center text-zinc-400 hover:text-[#D4AF37]">{paused ? <Play size={16} /> : <Pause size={16} />}</button>
        <button onClick={onExit} className="w-10 h-10 rounded-full border border-white/10 flex items-center justify-center text-zinc-400"><X size={16} /></button>
      </div>
      <button onClick={onStop} className="font-br-b text-xs text-zinc-600 hover:text-red-400">I need to stop</button>
    </motion.section>
  );
}

/* ═══ ONE-MINUTE RESET ═══ */
function OneMinuteReset({ exercise, reducedMotion, onFinish, onStop, onExit }) {
  const ex = exercise;
  const [started, setStarted] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [paused, setPaused] = useState(false);
  const [done, setDone] = useState(false);
  const intervalRef = useRef(null);

  useEffect(() => {
    if (!started || paused || done) { clearInterval(intervalRef.current); return; }
    intervalRef.current = setInterval(() => {
      setElapsed(prev => {
        const next = prev + 0.1;
        if (next >= 60) { setDone(true); clearInterval(intervalRef.current); return 60; }
        return next;
      });
    }, 100);
    return () => clearInterval(intervalRef.current);
  }, [started, paused, done]);

  const currentStep = (ex.steps || []).find(s => elapsed >= s.start && elapsed < s.end);
  const pct = (elapsed / 60) * 100;
  const circumference = 2 * Math.PI * 90;

  if (!started) {
    return (
      <motion.section initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="flex flex-col items-center justify-center min-h-[80vh] px-6 text-center">
        <h2 className="font-br-h text-3xl text-[#F5F5F0] mb-4">{ex.title}</h2>
        <p className="font-br-b text-sm text-zinc-400 mb-10">{ex.description}</p>
        <button onClick={() => setStarted(true)} data-testid="br-begin"
          className="bg-[#D4AF37] text-[#0A0A0A] px-12 py-5 rounded-full font-br-b tracking-[0.3em] uppercase text-lg hover:shadow-[0_0_30px_rgba(212,175,55,0.4)] transition-all">START NOW</button>
        <button onClick={onExit} className="font-br-b text-xs text-zinc-600 mt-4">Back</button>
      </motion.section>
    );
  }

  if (done) {
    return (
      <motion.section initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex flex-col items-center justify-center min-h-screen px-6 text-center" data-testid="br-reset-done">
        <h2 className="font-br-h text-2xl text-[#D4AF37] italic mb-8">{(ex.steps || []).at(-1)?.text}</h2>
        <div className="flex flex-col gap-3 max-w-xs w-full">
          {(ex.completion_options || []).map((opt, i) => (
            <button key={i} onClick={() => {
              if (opt.action === "restart") { setElapsed(0); setDone(false); }
              else if (opt.action === "selector") onExit();
              else onFinish(60);
            }} className="border border-white/10 text-zinc-300 px-6 py-3 rounded-full font-br-b text-sm hover:border-[#D4AF37]/40 transition-all">{opt.label}</button>
          ))}
        </div>
      </motion.section>
    );
  }

  return (
    <motion.section initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex flex-col items-center justify-center min-h-screen px-6 text-center" data-testid="br-active-one_minute_reset">
      {/* Circular timer */}
      <div className="relative w-56 h-56 mb-8">
        <svg viewBox="0 0 200 200" className="w-full h-full">
          <circle cx={100} cy={100} r={90} fill="none" stroke="rgba(255,255,255,0.04)" strokeWidth={3} />
          <circle cx={100} cy={100} r={90} fill="none" stroke="#D4AF37" strokeWidth={3}
            strokeDasharray={circumference} strokeDashoffset={circumference - (pct / 100) * circumference}
            strokeLinecap="round" transform="rotate(-90 100 100)" className="transition-all duration-100" />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="font-br-b text-3xl text-[#F5F5F0]">{Math.ceil(60 - elapsed)}</span>
        </div>
      </div>

      <AnimatePresence mode="wait">
        <motion.p key={currentStep?.text} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}
          className="font-br-h text-xl md:text-2xl text-[#F5F5F0] italic max-w-md mb-8 h-16 flex items-center">{currentStep?.text}</motion.p>
      </AnimatePresence>

      <div className="flex gap-3 mb-4">
        <button onClick={() => setPaused(!paused)} className="w-10 h-10 rounded-full border border-white/10 flex items-center justify-center text-zinc-400 hover:text-[#D4AF37]">{paused ? <Play size={16} /> : <Pause size={16} />}</button>
        <button onClick={onExit} className="w-10 h-10 rounded-full border border-white/10 flex items-center justify-center text-zinc-400"><X size={16} /></button>
      </div>
      <button onClick={onStop} className="font-br-b text-xs text-zinc-600 hover:text-red-400">I need to stop</button>
    </motion.section>
  );
}

/* ═══ STOPPED ═══ */
function StoppedView({ s, onBack }) {
  return (
    <motion.section initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="flex flex-col items-center justify-center min-h-[80vh] px-6 text-center" data-testid="br-stopped">
      <p className="font-br-h text-2xl text-[#F5F5F0] italic max-w-md mb-10">{s.stop_message}</p>
      <button onClick={onBack} className="border border-[#D4AF37] text-[#D4AF37] px-8 py-3 rounded-full font-br-b text-sm hover:bg-[#D4AF37] hover:text-[#0A0A0A] transition-all">Return to exercises</button>
    </motion.section>
  );
}

/* ═══ AFTER BREATH ═══ */
function AfterBreath({ s, exercise, exercises, onSelect, onJournal, onExit }) {
  const [picked, setPicked] = useState(null);
  return (
    <motion.section initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="flex flex-col items-center justify-center min-h-[80vh] px-6 text-center py-16" data-testid="br-after">
      <h2 className="font-br-h text-2xl text-[#F5F5F0] mb-8">{s.after_heading}</h2>
      <div className="flex flex-col gap-3 max-w-sm w-full mb-10">
        {(s.after_options || []).map((opt, i) => (
          <button key={i} onClick={() => setPicked(opt)} className={`border px-6 py-3 rounded-full font-br-b text-sm transition-all ${picked?.label === opt.label ? "border-[#D4AF37] text-[#D4AF37]" : "border-white/10 text-zinc-400 hover:border-zinc-600"}`}>{opt.label}</button>
        ))}
      </div>
      <p className="font-br-h text-lg text-zinc-400 italic max-w-lg mb-8">{s.after_message}</p>
      {s.after_prompts?.[0] && <p className="font-br-h text-xl text-[#D4AF37] italic mb-8">"{s.after_prompts[0]}"</p>}
      <div className="flex flex-wrap gap-3 justify-center">
        <button onClick={onExit} className="border border-white/10 text-zinc-400 px-5 py-2.5 rounded-full font-br-b text-xs hover:border-zinc-600 transition-all">Choose another exercise</button>
        <button onClick={onJournal} className="border border-white/10 text-zinc-400 px-5 py-2.5 rounded-full font-br-b text-xs hover:border-zinc-600 transition-all">Write privately</button>
        <Link to="/mirror-conversations" className="border border-[#D4AF37] text-[#D4AF37] px-5 py-2.5 rounded-full font-br-b text-xs hover:bg-[#D4AF37] hover:text-[#0A0A0A] transition-all">Learn about Mirror Conversations™</Link>
      </div>
    </motion.section>
  );
}

/* ═══ JOURNAL ═══ */
function Journal({ s, exercise, onBack }) {
  const [text, setText] = useState("");
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);

  const saveToPortal = async () => {
    setSaving(true);
    try {
      const clientToken = localStorage.getItem("br_client_token") || localStorage.getItem("client_token");
      if (!clientToken) { toast.error("Log in to your Client Portal to save reflections."); return; }
      await http.post("/breathing-room/public/save-reflection", { text, exercise: exercise?.title || "Breathing Room" }, { headers: { Authorization: `Bearer ${clientToken}` } });
      setSaved(true);
      toast.success("Saved to your portal.");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save. Make sure you're logged into the Client Portal."); }
    finally { setSaving(false); }
  };

  return (
    <motion.section initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="flex flex-col items-center justify-center min-h-[80vh] px-6 text-center max-w-xl mx-auto py-16" data-testid="br-journal">
      <h2 className="font-br-h text-2xl text-[#F5F5F0] mb-2">Private Reflection</h2>
      <p className="font-br-b text-xs text-zinc-500 mb-6">{s.journal_disclaimer}</p>
      <textarea value={text} onChange={e => setText(e.target.value)} rows={6} placeholder="Write whatever comes to mind..."
        className="w-full bg-transparent border border-white/10 rounded-xl px-4 py-3 text-sm text-[#F5F5F0] font-br-b focus:border-[#D4AF37] focus:outline-none resize-none mb-4" data-testid="br-journal-text" />
      <div className="flex gap-3">
        <button onClick={onBack} className="border border-white/10 text-zinc-400 px-5 py-2.5 rounded-full font-br-b text-xs">Back</button>
        {text.trim() && !saved && (
          <button onClick={saveToPortal} disabled={saving}
            className="border border-[#D4AF37] text-[#D4AF37] px-5 py-2.5 rounded-full font-br-b text-xs hover:bg-[#D4AF37] hover:text-[#0A0A0A] transition-all disabled:opacity-50">
            {saving ? "Saving..." : "Save to Portal"}
          </button>
        )}
        {saved && <span className="font-br-b text-xs text-green-400">Saved ✓</span>}
      </div>
    </motion.section>
  );
}
